import { useEffect, useReducer } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import {
  isValidTimeZone,
  startOfNextDayInTimeZone,
  todayInTimeZone,
} from '@/lib/date-utils';
import type {
  OrganizationSettings,
  User,
  Invitation,
  AssetTemplate,
  AuditLog,
  PaginatedResponse,
  CreateInvitationPayload,
  CreateAssetTemplatePayload,
  UserRole,
  ModulesResponse,
  ModulesConfig,
  MfaRequirementPreview,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;
const ORG_TIME_ZONE_STORAGE_KEY = 'h3ight-org-time-zone';

function orgTimeZoneStorageKey(organizationId: number): string {
  return `${ORG_TIME_ZONE_STORAGE_KEY}:${organizationId}`;
}

export function getStoredOrgTimeZone(organizationId: number | null | undefined): string | null {
  if (!organizationId) return null;

  try {
    const stored = localStorage.getItem(orgTimeZoneStorageKey(organizationId));
    return isValidTimeZone(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeOrgTimeZone(
  organizationId: number | null | undefined,
  timeZone: string
): void {
  if (!organizationId || !isValidTimeZone(timeZone)) return;

  try {
    localStorage.setItem(orgTimeZoneStorageKey(organizationId), timeZone);
  } catch {
    // Online settings remain available when browser storage is unavailable.
  }
}

interface OrganizationSettingsQueryOptions {
  requireFreshMoneyRecordCounts?: boolean;
}

export function useOrganizationSettings(
  options: OrganizationSettingsQueryOptions = {}
) {
  const { user } = useAuth();
  const refetchMoneyRecordCounts = options.requireFreshMoneyRecordCounts
    ? 'always'
    : undefined;

  return useQuery({
    queryKey: queryKeys.settings.organization(),
    queryFn: async (): Promise<OrganizationSettings> => {
      const response = await apiClient.get<OrganizationSettings>('/api/v1/org-settings/');
      storeOrgTimeZone(user?.organization, response.data.time_zone);
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    refetchOnMount: refetchMoneyRecordCounts,
    refetchOnWindowFocus: refetchMoneyRecordCounts,
  });
}

export function useOrgTimeZone(): string {
  const { user } = useAuth();
  const { data: settings } = useOrganizationSettings();
  if (isValidTimeZone(settings?.time_zone)) return settings.time_zone;
  return getStoredOrgTimeZone(user?.organization) ?? 'UTC';
}

export function useOrgCurrency(): string {
  const { data: settings } = useOrganizationSettings();
  return settings?.currency ?? 'USD';
}

export function useOrgToday(): string {
  const timeZone = useOrgTimeZone();
  const [, refresh] = useReducer((value: number) => value + 1, 0);
  const today = todayInTimeZone(timeZone);

  useEffect(() => {
    const delay =
      new Date(startOfNextDayInTimeZone(today, timeZone)).getTime() - Date.now() + 100;
    const timeout = window.setTimeout(refresh, Math.max(delay, 0));

    return () => window.clearTimeout(timeout);
  }, [timeZone, today]);

  return today;
}

export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<OrganizationSettings>): Promise<OrganizationSettings> => {
      const response = await apiClient.patch<OrganizationSettings>('/api/v1/org-settings/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.organization() });
    },
  });
}

export interface UsersFilters {
  role?: UserRole;
  is_active?: boolean;
  search?: string;
}

export function useUsers(filters: UsersFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.users.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<User>> => {
      const response = await apiClient.get<PaginatedResponse<User> | User[]>('/api/v1/users/', {
        params: cleanFilters,
      });
      const data = response.data;
      return Array.isArray(data)
        ? { count: data.length, next: null, previous: null, results: data }
        : data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useUser(id: number) {
  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: async (): Promise<User> => {
      try {
        const response = await apiClient.get<User>(`/api/v1/users/${id}/`);
        return response.data;
      } catch (err) {
        // TODO: remove after debugging User Not Found issue
        console.error(`[useUser] failed to fetch /api/v1/users/${id}/`, err);
        throw err;
      }
    },
    staleTime: FIVE_MINUTES,
    enabled: id > 0,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<User> }): Promise<User> => {
      const response = await apiClient.patch<User>(`/api/v1/users/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useAssignRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      role,
      confirmAdminGrant,
    }: {
      userId: number;
      role: UserRole;
      confirmAdminGrant?: boolean;
    }): Promise<User> => {
      const response = await apiClient.post<User>(
        `/api/v1/users/${userId}/assign_role/`,
        {
          role,
          ...(confirmAdminGrant ? { confirm_admin_grant: true } : {}),
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function usePreviewMfaRequirement() {
  return useMutation({
    mutationFn: async (required_mfa_roles: UserRole[]): Promise<MfaRequirementPreview> => {
      const response = await apiClient.post<MfaRequirementPreview>(
        '/api/v1/org-settings/mfa-requirement-preview/',
        { required_mfa_roles },
      );
      return response.data;
    },
  });
}

export function useInvitations() {
  return useQuery({
    queryKey: queryKeys.invitations.list({}),
    queryFn: async (): Promise<PaginatedResponse<Invitation>> => {
      const response = await apiClient.get<PaginatedResponse<Invitation>>('/api/v1/invitations/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvitationPayload): Promise<Invitation> => {
      const response = await apiClient.post<Invitation>('/api/v1/invitations/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invitations.all });
    },
  });
}

export function useDeleteInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/invitations/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invitations.all });
    },
  });
}

export function useAssetTemplates() {
  return useQuery({
    queryKey: queryKeys.assetTemplates.list(),
    queryFn: async (): Promise<PaginatedResponse<AssetTemplate>> => {
      const response = await apiClient.get<PaginatedResponse<AssetTemplate>>('/api/v1/asset-templates/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useCreateAssetTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAssetTemplatePayload): Promise<AssetTemplate> => {
      const response = await apiClient.post<AssetTemplate>('/api/v1/asset-templates/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assetTemplates.all });
    },
  });
}

export function useUpdateAssetTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateAssetTemplatePayload> }): Promise<AssetTemplate> => {
      const response = await apiClient.patch<AssetTemplate>(`/api/v1/asset-templates/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assetTemplates.all });
    },
  });
}

export function useDeleteAssetTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/asset-templates/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assetTemplates.all });
    },
  });
}

export interface AuditLogsFilters {
  start_date?: string;
  end_date?: string;
  user?: number;
  action?: string;
  model_name?: string;
  page?: number;
}

export function useAuditLogs(filters: AuditLogsFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.auditLogs.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<AuditLog>> => {
      const response = await apiClient.get<PaginatedResponse<AuditLog>>('/api/v1/audit-logs/', {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export async function exportAuditLogsCsv(filters: AuditLogsFilters = {}): Promise<Blob> {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(
      ([key, value]) => key !== 'page' && value !== '' && value !== undefined
    )
  );

  try {
    const response = await apiClient.get('/api/v1/audit-logs/export/', {
      params: { ...cleanFilters, export_format: 'csv' },
      responseType: 'blob',
    });
    return response.data as Blob;
  } catch (error) {
    const axiosError = error as {
      response?: { status?: number; data?: unknown };
    };
    const response = axiosError.response;
    if (!response) throw error;

    const responseData = response.data;
    let payload: unknown = responseData;
    if (
      responseData instanceof Blob ||
      (typeof responseData === 'object' &&
        responseData !== null &&
        'text' in responseData &&
        typeof responseData.text === 'function')
    ) {
      try {
        payload = JSON.parse(await (responseData as Blob).text());
      } catch {
        payload = null;
      }
    } else if (typeof responseData === 'string') {
      try {
        payload = JSON.parse(responseData);
      } catch {
        payload = null;
      }
    }
    if (payload && typeof payload === 'object') {
      const details = payload as { detail?: unknown; error?: unknown };
      const message = [details.detail, details.error].find(
        (value): value is string => typeof value === 'string' && value.length > 0
      );
      if (message) throw Object.assign(new Error(message), { cause: error });
    }

    if (response.status !== undefined) {
      throw Object.assign(
        new Error(`Audit log export failed (HTTP ${response.status}).`),
        { cause: error }
      );
    }
    throw error;
  }
}

export function useModules() {
  return useQuery({
    queryKey: queryKeys.settings.modules(),
    queryFn: async (): Promise<ModulesResponse> => {
      const response = await apiClient.get<ModulesResponse>('/api/v1/org-settings/modules/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useNukeUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      userId: number,
    ): Promise<{ message: string; account_deleted: boolean }> => {
      const response = await apiClient.delete<{
        message: string;
        account_deleted: boolean;
      }>(`/api/v1/users/${userId}/nuke/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useUpdateModules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (enabledModules: Partial<ModulesConfig>): Promise<OrganizationSettings> => {
      const response = await apiClient.patch<OrganizationSettings>(
        '/api/v1/org-settings/update_modules/',
        { enabled_modules: enabledModules }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.organization() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.modules() });
    },
  });
}

export function useSetSigningPin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, pin }: { userId: number; pin: string }): Promise<{ message: string }> => {
      const response = await apiClient.post<{ message: string }>(
        `/api/v1/users/${userId}/signing-pin/`,
        { pin }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useClearSigningPin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number): Promise<{ message: string }> => {
      const response = await apiClient.delete<{ message: string }>(
        `/api/v1/users/${userId}/signing-pin/`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}
